mutation ReplyToTicket($input: ReplyInput!) {
  replyToTicket(input: $input) {
    ...SupportMessageFields
  }
}

mutation UpdateSupportTicket($id: ID!, $expectedVersion: Int!, $input: UpdateTicketInput!) {
  updateTicket(id: $id, expectedVersion: $expectedVersion, input: $input) {
    ...SupportTicketFields
  }
}

# A request a client made outside the widget, logged by an agent. Its description is an
# internal note: the client hears nothing until someone replies publicly.
mutation LogSupportTicket($input: LogSupportTicketInput!) {
  logSupportTicket(input: $input) {
    ...SupportTicketFields
  }
}

# Leave a variable out to keep that half of the assignment; send null to clear it.
mutation AssignSupportTicket($id: ID!, $assigneeUserId: ID, $teamId: ID) {
  assignTicket(id: $id, assigneeUserId: $assigneeUserId, teamId: $teamId) {
    ...SupportTicketFields
  }
}

# The pull queue: take an unassigned ticket. CONFLICT names who got it first.
mutation TakeSupportTicket($id: ID!) {
  takeTicket(id: $id) {
    ...SupportTicketFields
  }
}

# Let the AI keep answering this chat beside the team, or stop it.
mutation SetSupportTicketAiAlongside($id: ID!, $enabled: Boolean!) {
  setTicketAiAlongside(id: $id, enabled: $enabled) {
    ...SupportTicketFields
  }
}

mutation CloseSupportTicket($id: ID!, $resolution: AgentTicketResolution!) {
  closeTicket(id: $id, resolution: $resolution) {
    ...SupportTicketFields
  }
}

mutation ReopenSupportTicket($id: ID!) {
  reopenTicket(id: $id) {
    ...SupportTicketFields
  }
}

mutation MarkSupportTicketRead($id: ID!, $upToMessageId: ID!) {
  markTicketRead(id: $id, upToMessageId: $upToMessageId)
}

mutation SetSupportTyping($ticketId: ID!, $internal: Boolean) {
  setTyping(ticketId: $ticketId, internal: $internal)
}

mutation CreateSupportAttachmentUpload(
  $ticketId: ID!
  $filename: String!
  $mimeType: String!
  $size: Int!
) {
  createSupportAttachmentUpload(
    ticketId: $ticketId
    filename: $filename
    mimeType: $mimeType
    size: $size
  ) {
    fileId
    uploadUrl
    expiresAt
    maxBytes
  }
}

mutation ConfirmSupportAttachmentUpload($ticketId: ID!, $fileId: ID!) {
  confirmSupportAttachmentUpload(ticketId: $ticketId, fileId: $fileId) {
    id
    filename
    mimeType
    size
    url
    previewUrl
  }
}

mutation MarkSupportNotificationsRead($ids: [ID!]) {
  markSupportNotificationsRead(ids: $ids)
}

mutation UpdateSupportNotificationPreferences($input: [SupportNotificationPreferenceInput!]!) {
  updateSupportNotificationPreferences(input: $input) {
    kind
    inApp
    email
  }
}

mutation UpdateSupportSettings($input: SupportSettingsInput!) {
  updateSupportSettings(input: $input) {
    ticketPrefix
    autoCloseHours
    aiEnabled
    aiDailySpendCapUsd
    clientEmailDelayMinutes
    clientReplyUrl
    widget {
      allowedOrigins
    }
  }
}

mutation UpsertStatusWorkflow($input: StatusWorkflowInput!) {
  upsertStatusWorkflow(input: $input) {
    id
    name
    updatedAt
    statuses {
      key
      name
      category
      order
      retired
    }
    transitions {
      from
      to
    }
  }
}

mutation UpsertTicketType($input: TicketTypeInput!) {
  upsertTicketType(input: $input) {
    id
    key
    name
    icon
    workflowId
    initialStatusKey
    closedStatusKey
    aiEnabled
    order
    retired
  }
}

mutation UpsertSupportRole($input: SupportRoleInput!) {
  upsertSupportRole(input: $input) {
    id
    key
    displayName
    description
    match {
      source
      attributeKey
      values
    }
    articleCount
    isPublic
    aiEnabled
    retired
    contentHints {
      paths
      sections
      keywords
    }
  }
}

mutation DeleteSupportRole($id: ID!) {
  deleteSupportRole(id: $id) {
    key
    articlesUpdated
    articlesHeldForReview
    guidelinesPaused
  }
}

# Sandbox: a hidden practice workspace, and tokens for practice customers of it.
mutation EnsureSupportSandbox {
  ensureSupportSandbox {
    ...SupportSandboxFields
  }
}

mutation ResetSupportSandbox {
  resetSupportSandbox {
    ...SupportSandboxFields
  }
}

mutation CreateSupportSandboxToken($persona: SupportSandboxPersonaInput!) {
  createSupportSandboxToken(persona: $persona) {
    token
    expiresAt
    externalUserId
    sandboxTenantId
  }
}

mutation CreateWebhookEndpoint($url: String!, $events: [WebhookEventType!]!) {
  createWebhookEndpoint(url: $url, events: $events) {
    secret
    endpoint {
      ...WebhookEndpointFields
    }
  }
}

mutation UpdateWebhookEndpoint(
  $id: ID!
  $url: String
  $events: [WebhookEventType!]
  $enabled: Boolean
) {
  updateWebhookEndpoint(id: $id, url: $url, events: $events, enabled: $enabled) {
    ...WebhookEndpointFields
  }
}

mutation RotateWebhookEndpointSecret($id: ID!) {
  rotateWebhookEndpointSecret(id: $id) {
    secret
    endpoint {
      ...WebhookEndpointFields
    }
  }
}

mutation DeleteWebhookEndpoint($id: ID!) {
  deleteWebhookEndpoint(id: $id)
}

mutation SendWebhookEndpointTest($id: ID!) {
  sendWebhookEndpointTest(id: $id) {
    ok
    statusCode
    error
  }
}

mutation ReplayWebhookDelivery($deliveryId: ID!) {
  replayWebhookDelivery(deliveryId: $deliveryId) {
    id
    status
    attempts
    replayCount
    nextAttemptAt
    lastError
    lastStatusCode
    deliveredAt
  }
}

mutation LinkWorkItem($ticketId: ID!, $url: String!) {
  linkWorkItem(ticketId: $ticketId, url: $url) {
    ...WorkItemLinkFields
  }
}

mutation UnlinkWorkItem($linkId: ID!) {
  unlinkWorkItem(linkId: $linkId)
}

mutation ResyncWorkItem($linkId: ID!) {
  resyncWorkItem(linkId: $linkId) {
    ...WorkItemLinkFields
  }
}

mutation CreateWorkItemFromTicket($ticketId: ID!, $listId: ID) {
  createWorkItemFromTicket(ticketId: $ticketId, listId: $listId) {
    ...WorkItemLinkFields
  }
}

mutation UpdateClientStages($input: ClientStagesInput!, $expectedVersion: Int!) {
  updateClientStages(input: $input, expectedVersion: $expectedVersion) {
    version
    isDefault
    notifyAfterClose
    commentOnLinkedTasks
    stages {
      key
      label
      notifyClient
      template
      announceRegressions
      moveToStatusKey
    }
  }
}

mutation UpsertWorkItemStatusMap($listId: ID, $entries: [StatusMapEntryInput!]!) {
  upsertWorkItemStatusMap(listId: $listId, entries: $entries) {
    listId
    updatedAt
    entries {
      externalStatus
      stageKey
    }
  }
}

mutation SetWorkItemRouting($ticketTypeId: ID!, $listId: ID) {
  setWorkItemRouting(ticketTypeId: $ticketTypeId, listId: $listId) {
    ticketTypeId
    listId
  }
}

mutation CreateKnowledgeSource($input: KnowledgeSourceInput!) {
  createKnowledgeSource(input: $input) {
    id
  }
}

mutation ConnectKnowledgeSource($input: ConnectKnowledgeSourceInput!) {
  connectKnowledgeSource(input: $input) {
    id
  }
}

mutation ConnectClickUpKnowledgeSource($input: ConnectClickUpKnowledgeSourceInput!) {
  connectClickUpKnowledgeSource(input: $input) {
    id
  }
}

mutation CrawlKnowledgeSource($sourceId: ID!, $force: Boolean, $reguessAudience: Boolean) {
  crawlKnowledgeSource(sourceId: $sourceId, force: $force, reguessAudience: $reguessAudience) {
    id
    status
  }
}

mutation DeleteKnowledgeSource($sourceId: ID!) {
  deleteKnowledgeSource(sourceId: $sourceId)
}

mutation SetKnowledgeArticleAudience($articleId: ID!, $audience: KbAudienceInput!) {
  setKnowledgeArticleAudience(articleId: $articleId, audience: $audience) {
    id
    status
    audience {
      roles
      plans
      source
      confidence
      evidence
    }
  }
}

mutation ExcludeKnowledgeArticle($articleId: ID!, $reason: String) {
  excludeKnowledgeArticle(articleId: $articleId, reason: $reason) {
    id
    status
  }
}

mutation UpdateKnowledgeSource($sourceId: ID!, $input: KnowledgeSourceEditInput!) {
  updateKnowledgeSource(sourceId: $sourceId, input: $input) {
    id
    include
    exclude
    schedule
    trustAudienceLines
  }
}

mutation BulkExcludeKnowledgeArticles($target: KbArticleBulkTargetInput!, $reason: String) {
  bulkExcludeKnowledgeArticles(target: $target, reason: $reason) {
    matched
    affected
    failed
  }
}

mutation BulkDeleteKnowledgeArticles($target: KbArticleBulkTargetInput!) {
  bulkDeleteKnowledgeArticles(target: $target) {
    matched
    affected
    failed
  }
}

mutation BulkSetKnowledgeArticleAudience(
  $target: KbArticleBulkTargetInput!
  $audience: KbAudienceInput!
) {
  bulkSetKnowledgeArticleAudience(target: $target, audience: $audience) {
    matched
    affected
    failed
  }
}

mutation AddKnowledgeDocument($input: KnowledgeDocumentInput!) {
  addKnowledgeDocument(input: $input) {
    id
  }
}

mutation StartIntercomImport($input: StartIntercomImportInput!) {
  startIntercomImport(input: $input) {
    ...SupportImportRunFields
  }
}

mutation CancelSupportImport($id: ID!) {
  cancelSupportImport(id: $id) {
    ...SupportImportRunFields
  }
}

mutation StartSupportCompare($input: SupportCompareRangeInput!) {
  startSupportCompare(input: $input) {
    ...SupportCompareRunFields
  }
}

mutation CancelSupportCompare($id: ID!) {
  cancelSupportCompare(id: $id) {
    ...SupportCompareRunFields
  }
}

mutation ResumeSupportCompare($id: ID!) {
  resumeSupportCompare(id: $id) {
    ...SupportCompareRunFields
  }
}

mutation SetKnowledgeAudienceRules(
  $sourceId: ID!
  $rules: [KbAudienceRuleInput!]!
  $applyToExisting: Boolean
) {
  setKnowledgeAudienceRules(sourceId: $sourceId, rules: $rules, applyToExisting: $applyToExisting) {
    checked
    changed
    source {
      id
      audienceRules {
        conditions {
          match
          value
        }
        roles
        everyone
      }
    }
  }
}

# Guidelines: the support team's instructions to the AI.
mutation SaveSupportGuideline($input: SupportGuidelineInput!) {
  saveSupportGuideline(input: $input) {
    id
    kind
    title
    body
    roles
    live
    pausedReason
    updatedAt
  }
}

mutation DeleteSupportGuideline($id: ID!) {
  deleteSupportGuideline(id: $id)
}
